import { Sort } from "@models";
import { randomNumber } from "@core/utils/number";

export function compareStrings<T>(orderArray: T[], a: T, b: T): number {
    const aIndex = orderArray.indexOf(a);
    const bIndex = orderArray.indexOf(b);

    if (aIndex === -1 && bIndex === -1) {
        return 0;
    } else if (aIndex === -1) {
        return 1;
    } else if (bIndex === -1) {
        return -1;
    } else {
        return aIndex - bIndex;
    }
}

export const defaultSort = (direction: Sort) => (a: any, b: any) => {
    // force null and undefined to the bottom
    const directionCoefficient = direction === Sort.Asc ? 1 : -1;
    a = a ?? directionCoefficient * Infinity;
    b = b ?? directionCoefficient * Infinity;
    // force any string values to lowercase
    a = typeof (a) === 'string' ? a.toLowerCase() : a;
    b = typeof (b) === 'string' ? b.toLowerCase() : b;
    // Return either 1 or -1 to indicate a sort priority
    if (a > b) {
        return directionCoefficient;
    }
    if (a < b) {
        return -directionCoefficient;
    }
    return 0;
};

export function toggleInArray<T>(array: T[], item: T): T[] {
    if (array.includes(item)) {
        return array.filter(i => i !== item);
    } else {
        return [...array, item];
    }
}

export function excludeIndexes<T>(array: T[], indexes: number[]): T[] {
    return array.filter((_, index) => !indexes.includes(index));
}

export function containsSome<T>(array1: T[], array2: T[]): boolean {
    return array2.some(item => array1.includes(item));
}

export function containsAll<T>(array1: T[], array2: T[]): boolean {
    return array2.every(item => array1.includes(item));
}

export function getUnique<T>(array: T[]): T[] {
    return Array.from(new Set(array));
}

export function getDifference<T>(array1: T[], array2: T[]): T[] {
    return array1.filter(item => !array2.includes(item));
}

export function getIntersection<T>(array1: T[], array2: T[]): T[] {
    return array1.filter(item => array2.includes(item));
}

export function findItemByKey<T, K extends keyof T>(
    items: T[],
    value: any,
    key: K = "id" as K,
): T | undefined {
    return items.find((item) => item[key] !== undefined && item[key] === value) ?? undefined;
}

export function filterItemsByKey<T, K extends keyof T>(
    items: T[],
    values?: T[K][],
    key: K = "id" as K,
): T[] {
    return Array.isArray(values) ? items.filter((item) => item[key] != null && values.includes(item[key] as T[K])) : items;
}

export function filterSortedItemsByKey<T, K extends keyof T>(
    items: T[],
    values?: T[K][],
    key: K = "id" as K,
): T[] {
    const filteredItems = filterItemsByKey(items, values, key);
    return filteredItems.sort((a, b) => compareStrings(values ?? [], a[key], b[key]));
}

export function mapByKey<T, K extends keyof T>(items: T[], key: K): T[K][] {
    return items.map(item => item[key]) as T[K][];
}

export function excludeNullable<T>(items: (T | null | undefined)[]): T[] {
    return items.filter(item => item !== null && item !== undefined) as T[];
}

export function randomizeItems<T>(items: T[], count?: number): T[] {
    // Generate a random number between 0 and 1 and then deduct half to get positive/negative for random sort
    return items.sort(() => randomNumber() - 0.5).slice(0, count ?? items.length);
}